import {
    OneTimeCodeModel,
    ModelOneTimeCodeFields,
    ModelUserFields,
} from "@/models";

import crypto from "crypto";
import { dayjs } from "@/utils";

import { QueryList, QueryGet } from "@/schema/common";

import { buildSqlRelation, buildSqlRawSelect, buildSqlWhere, buildSqlSelect, buildSqlLimitOffset, buildSqlOrderBy } from "@/lib";

import { InjectionFieldModuleRef } from "@/services";

import { DefaultServiceProps, getCurrentDateTimeISO, throwError } from "@/utils";

export type OneTimeCodeModuleRef = InjectionFieldModuleRef

export type OneTimeCodeServiceProps = DefaultServiceProps & OneTimeCodeModuleRef & {
};

export const oneTimeCodeTypes = {
    token: "token",
    otp: "otp"
} as const

export type OneTimeCodeType = typeof oneTimeCodeTypes[keyof typeof oneTimeCodeTypes]

export const oneTimeCodeStatus = {
    active: "active",
    used: "used",
    revoked: "revoked"
} as const

export type OneTimeCodeStatus = typeof oneTimeCodeStatus[keyof typeof oneTimeCodeStatus]

export const oneTimeCodePurposeTypes = {
    reset_password: "reset_password",
    signup_account: "signup_account"
} as const

export type OneTimeCodePurposeType = typeof oneTimeCodePurposeTypes[keyof typeof oneTimeCodePurposeTypes]

export type OneTimeCodeCreateProps = InjectionFieldModuleRef & {
    data: {
        purposeType: OneTimeCodePurposeType,
        codeType: OneTimeCodeType,
        expiredAt?: Date,
        mobileNumber?: string,
        emailAddress?: string,
        userId?: number
        userType?: string
    }
}

export type OneTimeCodeGetProps = QueryGet & {
    id: number,
};

export type OneTimeCodeListProps = QueryList & {
    revealCode?: boolean
};

export type OneTimeCodeConsumeProps = InjectionFieldModuleRef & {
    data: {
        purposeType: OneTimeCodePurposeType
        oneTimeCode: string
        userId?: number
        userType?: string
    }
}

const getCreatePayload = async (
    fieldRef: OneTimeCodeCreateProps["field_ref"],
    fieldModule: OneTimeCodeCreateProps["field_module"],
    data: OneTimeCodeCreateProps["data"],
    code: string,
) => {

    let expiredAt = data?.expiredAt ?? ""

    if (!expiredAt) {
        // Set expiration 15 minutes from now - Default
        expiredAt = dayjs().add(15, "minute").toDate(); // convert to JS Date
    }

    return {
        [`${ModelOneTimeCodeFields.code_type}`]: data.codeType,
        [`${ModelOneTimeCodeFields.code}`]: code,
        [`${ModelOneTimeCodeFields.expires_at}`]: expiredAt,
        [`${ModelOneTimeCodeFields.mobile_number}`]: data.mobileNumber ?? "",
        [`${ModelOneTimeCodeFields.email_address}`]: data.emailAddress ?? "",
        [`${ModelOneTimeCodeFields.field_ref}`]: fieldRef,
        [`${ModelOneTimeCodeFields.field_module}`]: fieldModule,
        [`${ModelOneTimeCodeFields.user_id}`]: data?.userId ?? 0,
        [`${ModelOneTimeCodeFields.user_type}`]: data?.userType ?? "",
        [`${ModelOneTimeCodeFields.code_status}`]: oneTimeCodeStatus.active,
        [`${ModelOneTimeCodeFields.purpose_type}`]: data.purposeType ?? ""
    }

}

let mainSqlSelect = {
    [`otp.${ModelOneTimeCodeFields.one_time_code_id}`]: "one_time_code_id",
    [`otp.${ModelOneTimeCodeFields.purpose_type}`]: "purpose_type",
    [`otp.${ModelOneTimeCodeFields.code_status}`]: "code_status",
    [`otp.${ModelOneTimeCodeFields.code_type}`]: "code_type",
    [`otp.${ModelOneTimeCodeFields.email_address}`]: "email_address",
    [`otp.${ModelOneTimeCodeFields.mobile_number}`]: "mobile_number",
    [`otp.${ModelOneTimeCodeFields.expires_at}`]: "expires_at",
    [`otp.${ModelOneTimeCodeFields.used_at}`]: "used_at",
    [`otp.${ModelOneTimeCodeFields.field_module}`]: "field_module",
    [`otp.${ModelOneTimeCodeFields.field_ref}`]: "field_ref",
    [`otp.${ModelOneTimeCodeFields.user_id}`]: "user_id",
    [`otp.${ModelOneTimeCodeFields.user_type}`]: "user_type",
}

let mainCodeSqlSelect = {
    [`otp.${ModelOneTimeCodeFields.code}`]: "code"
}

let defaultSqlSelect = {
    [`otp.${ModelOneTimeCodeFields.createdatetime}`]: "createdatetime",
    [`creator.${ModelUserFields.login_username}`]: "createusername",
    [`otp.${ModelOneTimeCodeFields.updatedatetime}`]: "updatedatetime",
    [`updater.${ModelUserFields.login_username}`]: "updateusername",
    [`otp.${ModelOneTimeCodeFields.isdelete}`]: "isdelete",
    [`otp.${ModelOneTimeCodeFields.istrash}`]: "istrash",
    [`otp.${ModelOneTimeCodeFields.accountid}`]: "accountid",
}

export const OneTimeCodeService = (props: OneTimeCodeServiceProps) => {

    const {
        field_ref = "document",
        field_module = "sales",
        ...rest
    } = props

    const oneTimeCodeModel = OneTimeCodeModel({ ...rest })

    const generateOneTimeCode = ({
        codeType = "otp"
    }: {
        codeType: OneTimeCodeType
    }) => {

        if (codeType === "otp") {
            // Generate 6-digit numeric OTP
            const otp = Math.floor(100000 + Math.random() * 900000);
            return otp.toString();
        } else if (codeType === "token") {
            // Generate secure random 32-character alphanumeric token
            return crypto.randomBytes(16).toString("hex"); // 16 bytes = 32 hex chars
        }

        throw new Error(`Unsupported codeType: ${codeType}`);

    }

    const createOneTimeCode = async ({ data, field_ref: overrideFieldRef, field_module: overrideFieldModule }: OneTimeCodeCreateProps) => {

        const _fieldRef = overrideFieldRef || field_ref
        const _fieldModule = overrideFieldModule || field_module

        const oneTimeCode = generateOneTimeCode({
            codeType: data.codeType
        })

        const _data = await getCreatePayload(
            _fieldRef,
            _fieldModule,
            data,
            oneTimeCode
        )

        if (!_data) {
            throw new Error(`Invalid create one time code payload: ${JSON.stringify(_data)}`);
        }

        // create one time code
        let response = await oneTimeCodeModel.create({ data: _data })

        return response
    }

    const getOneTimeCode = async ({ id, datatypes = [] }: OneTimeCodeGetProps) => {

        let { data } = await listOneTimeCodes({
            limit: 1,
            offset: 0,
            filters: [{ field: `${ModelOneTimeCodeFields.one_time_code_id}`, operator: "=", value: id }],
            sortfield: `${ModelOneTimeCodeFields.one_time_code_id}`,
            sortorder: "ASC",
            datatypes,
            revealCode: false
        })

        return data[0] ?? null;

    };

    const listOneTimeCodes = async ({ limit, offset, filters = [], sortfield, sortorder, datatypes = [], revealCode = false }: OneTimeCodeListProps) => {

        let data = []

        let sqlSelect = [
            `COUNT(*) OVER() as filtered_count`,
            buildSqlSelect(``, mainSqlSelect),
            ...(revealCode === true ? [buildSqlSelect(``, mainCodeSqlSelect)] : []),
            buildSqlSelect(``, defaultSqlSelect)
        ]

        let sqlRelation = [
            // buildSqlRelation("left", "otp", "accounts", "acc", ["otp.accountid = acc.account_id", "acc.isdelete = false", "acc.istrash = false"]),
            buildSqlRelation("left", "otp", "users", "creator", ["otp.createuserid = creator.user_id", "creator.isdelete = false", "creator.istrash = false"]),
            buildSqlRelation("left", "otp", "users", "updater", ["otp.updateuserid = updater.user_id", "updater.isdelete = false", "updater.istrash = false"])
        ]

        let sqlGroupBy: string[] = [
            ...Object.keys(mainSqlSelect),
            ...(revealCode === true ? Object.keys(mainCodeSqlSelect) : []),
            ...Object.keys(defaultSqlSelect)
        ]

        let filterColumns = {
            ...mainSqlSelect,
            ...(revealCode === true ? mainCodeSqlSelect : {}),
            ...defaultSqlSelect
        }

        let sqlWhere = [
            `otp.isdelete = false`,
            `otp.istrash = false`,
            ...(filters.length ? [buildSqlWhere(filters, filterColumns)?.where] : []),
        ]

        let sqlOrderby = buildSqlOrderBy(sortfield, sortorder)
        let sqlLimitOffset = buildSqlLimitOffset(limit, offset)

        const sqlRaw = buildSqlRawSelect({
            tableName: "one_time_codes",
            mainAlias: "otp",
            accountId: rest.accountId,
            sqlSelect,
            sqlRelation,
            sqlWhere,
            sqlGroupBy,
            sqlOrderby,
            sqlLimitOffset,
        })

        data = await oneTimeCodeModel.raw(sqlRaw)

        const response = {
            data: data.map(({ filtered_count, ...rest }) => rest),
            total: data.length ? Number(data[0].filtered_count) : 0
        }

        return response

    };

    const consumeOneTimeCode = async ({ data, field_ref: overrideFieldRef, field_module: overrideFieldModule }: OneTimeCodeConsumeProps) => {

        const _fieldRef = overrideFieldRef || field_ref
        const _fieldModule = overrideFieldModule || field_module

        // retrive the one time code
        const { data: findOneTimeCodes } = await listOneTimeCodes({
            limit: 1,
            offset: 0,
            filters: [
                {
                    field: `${ModelOneTimeCodeFields.field_ref}`,
                    operator: "=",
                    value: `${_fieldRef}`
                },
                {
                    field: `${ModelOneTimeCodeFields.field_module}`,
                    operator: "=",
                    value: `${_fieldModule}`
                },
                {
                    field: `${ModelOneTimeCodeFields.purpose_type}`,
                    operator: "=",
                    value: `${data.purposeType}`
                },
                {
                    field: `${ModelOneTimeCodeFields.code}`,
                    operator: "=",
                    value: `${data.oneTimeCode}`
                },
                ...(
                    data?.userId ? [{
                        field: `${ModelOneTimeCodeFields.user_id}`,
                        operator: "=",
                        value: `${data.userId}`
                    }] as OneTimeCodeListProps['filters'] : []
                ),
                ...(
                    data?.userType ? [{
                        field: `${ModelOneTimeCodeFields.user_type}`,
                        operator: "=",
                        value: `${data.userType}`
                    }] as OneTimeCodeListProps['filters'] : []
                )
            ],
            sortfield: `${ModelOneTimeCodeFields.one_time_code_id}`,
            sortorder: "ASC",
            datatypes: [],
            revealCode: true
        })

        const findOneTimeCode = findOneTimeCodes.length > 0 ? findOneTimeCodes[0] : {}
        const findOneTimeCodeId = Number(findOneTimeCode?.one_time_code_id ?? "")

        if (!findOneTimeCodeId) {
            throw new Error(`Invalid one time code: ${data.oneTimeCode}`);
        }

        const findOneTimeCodeStatus = findOneTimeCode?.code_status

        if (findOneTimeCodeStatus !== oneTimeCodeStatus.active) {
            throw new Error(`One time code is not active: ${data.oneTimeCode}`);
        }

        const currentDateTimeISO = getCurrentDateTimeISO()

        // mark one time code as used
        await oneTimeCodeModel.update({
            id: findOneTimeCodeId,
            data: {
                [`${ModelOneTimeCodeFields.code_status}`]: oneTimeCodeStatus.used,
                [`${ModelOneTimeCodeFields.used_at}`]: currentDateTimeISO
            }
        })

        // revoke all related ACTIVE one time codes
        await oneTimeCodeModel.update({
            data: {
                [`${ModelOneTimeCodeFields.code_status}`]: oneTimeCodeStatus.revoked,
                [`${ModelOneTimeCodeFields.used_at}`]: currentDateTimeISO
            },
            where: {
                [`${ModelOneTimeCodeFields.field_ref}`]: _fieldRef,
                [`${ModelOneTimeCodeFields.field_module}`]: _fieldModule,
                [`${ModelOneTimeCodeFields.purpose_type}`]: data.purposeType,
                [`${ModelOneTimeCodeFields.code_status}`]: oneTimeCodeStatus.active,
                ...(data?.userId ? {
                    [`${ModelOneTimeCodeFields.user_id}`]: data.userId,
                } : {}),
                ...(data?.userType ? {
                    [`${ModelOneTimeCodeFields.user_type}`]: data.userType,
                } : {}),
            },
            disableRollback: true
        })

        let response = await getOneTimeCode({ id: findOneTimeCodeId, datatypes: [] })

        return response
    }

    return {
        createOneTimeCode,
        getOneTimeCode,
        listOneTimeCodes,
        consumeOneTimeCode
    }

}

